import React, { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AnyAssetApplication,
  capitalizeFirstLetter,
  ColumnsConfig,
  CompactTable,
  defaultResponseHandler,
  ExtraFilterConfig,
  insertArrayIf,
  isSiemensOpcDcsPlcSubtype,
  Page,
  PermissionGuard,
  siteActionsColumn,
  useAssetConfig,
  useAssetTranslation,
  useFilters,
  usePageQuery,
} from '@mss/mss-component-library';
import Breadcrumbs from '../../Breadcrumbs';
import LicenseBadge from '../../LicenseBadge';
import Button from '../../Button';
import { sanitizedUrl } from '../../../asset/util/sanitize';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useHistory, useRouteMatch, useLocation } from 'react-router-dom';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import RemoteConnectModal from '../../../asset/components/RemoteConnectModal';
import PageSidebar from '../../FilterSidebar';
import Fetch from '../../util/_fetch';
import MoreActionsMenu from 'jsx/asset/components/MoreActionMenu';
import type { TFunction } from 'i18next';
import {
  createCustomHeadingLabel,
  createCustomOptionLabels,
  getFilterKeysFromColumns,
  getSearchKeysFromColumns,
} from '../utils/filterHelpers';
import { openLinkInNewTab } from '../../util/openNewTab';
import { useConfigurationQuery } from '../../../index';

const defaultQueryOptions = {
  staleTime: 5000,
  refetchInterval: 5000,
  refetchIntervalInBackground: false,
};

const defaultExtraFilterKeys: ExtraFilterConfig<AnyAssetApplication>[] = [
  {
    key: 'heartbeat_status',
    customHeading: (t: TFunction) => t('Heartbeat Status'),
    customOption: value => capitalizeFirstLetter(String(value)),
    top: true,
  },
];
const DEFAULT_PAGE_SIZE = 10;
function List<
  AssetType extends AnyAssetApplication & { [key: string]: any },
>() {
  const {
    config: {
      listView: {
        rowKey,
        columns,
        subTypeKey,
        addButtonLabel,
        assetType,
        pageSize: initialPageSize,
        extraFilterKeys: _extraFilterKeys,
        extraSearchKeys,
      },
      name,
      permissions,
      licenseModule,
    },
  } = useAssetConfig<AssetType>();
  const { data: config } = useConfigurationQuery();

  const extraFilterKeys = [
    ...(defaultExtraFilterKeys as ExtraFilterConfig<AssetType>[]),
    ...(_extraFilterKeys ?? []),
  ].reduce((acc, x) => {
    if (!x) return acc;
    return [
      ...acc,
      {
        ...x,
        top: !!x.top,
        order: !!x.top ? 1 : 3,
      },
    ];
  }, [] as ExtraFilterConfig<AssetType>[]);

  const { t } = useAssetTranslation();
  const history = useHistory();
  const { url } = useRouteMatch();
  const [connectModal, openConnectModal] = useState<string | null>(null);
  const _columns = useMemo(
    (): ColumnsConfig<AssetType>[] => [
      ...columns({ t, openConnectModal }, subTypeKey),
      ...(insertArrayIf(!config?.unidirectional, [
        siteActionsColumn({ t, openConnectModal }),
      ]) as any),
    ],
    [columns, t, subTypeKey]
  );

  const { isLoading, data } = useQuery({
    ...defaultQueryOptions,
    queryKey: ['list', assetType],
    queryFn: async (): Promise<AssetType[]> => {
      const { data, error } = await defaultResponseHandler<AssetType[]>({
        t,
        request: Fetch.get(`/api/assets/?type=${assetType}&deleted=false`),
      });
      if (error) return [];
      return data.map(
        asset =>
          ({
            ...asset,
            // @ts-ignore
            custodian_name: asset.custodian?.name ?? '-',
            heartbeat_status:
              asset.type === 'dcs_plc' &&
              isSiemensOpcDcsPlcSubtype(asset.dcs_plc_type)
                ? t('N/A')
                : asset.heartbeat?.state ?? t('Unknown'),
            ...(asset.series
              ? {
                series_name: asset.series.name,
                brand_name: asset.series.brand_name,
              }
              : {}),
          }) as AssetType
      );
    },
  });

  const filterKeys = useMemo(
    () => getFilterKeysFromColumns<AssetType>(_columns, extraFilterKeys),
    [_columns, extraFilterKeys]
  );

  const searchKeys = useMemo(
    () => getSearchKeysFromColumns<AssetType>(_columns, extraSearchKeys),
    [_columns, extraSearchKeys]
  );

  const [availableFilters, toggleFilter, filteredData, search, setFilters] =
    useFilters<AssetType>(
      data ?? ([] as AssetType[]),
      filterKeys,
      searchKeys,
      createCustomOptionLabels<AssetType>(_columns, extraFilterKeys),
      createCustomHeadingLabel<AssetType>(_columns, t, extraFilterKeys)
    );
  const [currentPageSize, setCurrentPageSize] = useState<number>(
    initialPageSize ?? DEFAULT_PAGE_SIZE
  );
  const [page, goToPage] = usePageQuery(filteredData.length / (currentPageSize ?? 10));

  const rowOnClick = (record: AssetType, newTab?: boolean) => (e: any) => {
    const newUrl = `${sanitizedUrl(url)}/${record[subTypeKey as keyof AssetType]}/${record[rowKey ?? ('id' as keyof AssetType)]
      }/asset-settings`;
    const method =
      newTab || e.metaKey || e.ctrlKey ? openLinkInNewTab : history.push;
    method(newUrl);
  };

  const pagePermissions = useMemo(
    () =>
      permissions.reduce((acc: string[], curr) => {
        return [
          ...acc,
          ...['view', 'add', 'change', 'delete'].map(x => `${x}_${curr}`),
        ];
      }, []),
    [permissions]
  );

  const addPermissions = useMemo(
    () => permissions.map(x => `add_${x}`),
    [permissions]
  );
  const viewPermissions = useMemo(
    () => permissions.map(x => `view_${x}`),
    [permissions]
  );
  const location = useLocation();
  useEffect(() => {
    search.set('');
    setFilters({});
    setCurrentPageSize(initialPageSize ?? DEFAULT_PAGE_SIZE);
    goToPage(1);
  }, [location.pathname]);
  return (
    <PermissionGuard any codename={pagePermissions}>
      <Page
        breadCrumbs={<Breadcrumbs />}
        padding={false}
        actions={
          <>
            <PermissionGuard any codename={addPermissions}>
              <LicenseBadge module={licenseModule} dataSource={data}>
                <Button
                  onClick={() => history.push(`${sanitizedUrl(url)}/add`)}
                  data-ta="newAssetButton"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-1" />
                  {t(addButtonLabel ?? `Add ${name}`).toUpperCase()}
                </Button>
              </LicenseBadge>
            </PermissionGuard>
            <MoreActionsMenu count={data?.length} type={assetType} />
          </>
        }
        main={
          <PermissionGuard any codename={viewPermissions}>
            <CompactTable
              bordered={false}
              dataSource={filteredData}
              loading={isLoading}
              columns={_columns}
              rowKey={rowKey ?? 'id'}
              pagination={{
                current: page,
                pageSize: currentPageSize,
                showQuickJumper: true,
                showSizeChanger: true,
                total: filteredData.length,
                showTotal: (total: number, range: any[]) =>
                  t('(RANGE1)-(RANGE2) of (TOTAL) items', {
                    range1: range[0],
                    range2: range[1],
                    TOTAL: total,
                  }),
                onChange: (currentPage: number, newPageSize: number) => {
                  if (currentPage !== page) {
                    goToPage(currentPage);
                  }
                  if (newPageSize !== currentPageSize) {
                    setCurrentPageSize(newPageSize);
                    const newTotalPages = Math.ceil(filteredData.length / newPageSize);
                    if (page > newTotalPages && newTotalPages > 0) {
                      goToPage(newTotalPages);
                    }
                  }
                },
              }}
              onRow={(record: AssetType) => {
                return {
                  onClick: rowOnClick(record),
                  onAuxClick: rowOnClick(record, true),
                };
              }}
            />
            {!config?.unidirectional && (
              <RemoteConnectModal
                closeModal={() => openConnectModal(null)}
                asset={filteredData.find(x => x.id === connectModal) as any}
                visible={!!connectModal}
              />
            )}
          </PermissionGuard>
        }
        sidebar={
          <PageSidebar
            key={location.pathname}
            availableFilters={availableFilters}
            toggleFilter={toggleFilter}
            search={search}
          />
        }
      />
    </PermissionGuard>
  );
}

export default List;
